import { produce } from 'immer';
import type {
    PCConversation,
    PCConversationSummary,
    PCMessage,
    PCActionCard,
    PCChannel,
    PCLogEntry,
    PCCta,
    PCChatbotIdentity,
} from '../../../interface/ProductCatalogChatbotInterface';
import * as T from './ProductCatalogChatbotConstants';

/**
 * State for the Product Catalog Control Center AI chatbot (Brisque AI).
 * Kept under the `ProductCatalogChatbot` root key — separate from `ReportsChatbot`.
 */
export interface ProductCatalogChatbotState {
    // Active thread (§4.1 / §4.3)
    /** The outlet this slice's data belongs to. Kept in REDUX, not in a component ref: the
     *  chatbot is conditionally rendered, so navigating away unmounts it and destroys any ref,
     *  which is how an outlet switch made off-screen went undetected on return. */
    locationId: string | null;

    activeConversationId: string | null;
    conversation: PCConversation | null;
    conversationLoading: boolean;
    conversationFailure: boolean;

    // Sidebar list (§4.2)
    conversations: PCConversationSummary[];
    conversationsLoading: boolean;
    conversationsFailure: boolean;

    // Channel id → name lookup (§4.8)
    channels: PCChannel[];
    channelsLoaded: boolean;

    // Backend-served quick-action CTAs shown on the intro screen (§Item 6)
    ctas: PCCta[];
    ctasLoaded: boolean;

    // Activity log (§8)
    log: PCLogEntry[];
    logLoading: boolean;

    // In-flight flags
    createLoading: boolean;
    sending: boolean;
    cardActionLoading: boolean;
    /** A row-level card EDIT (channels / duration / remove-row) in flight. Separate from
     *  `cardActionLoading` on purpose: these edits are applied optimistically, so the user
     *  already sees the result and there is nothing to wait for. Folding them into the global
     *  flag put the whole composer into "Waiting for response…" and re-rendered the thinking
     *  state on every channel chip tap. Kept as its own flag so a caller that genuinely needs
     *  to know an edit is in flight still can. */
    cardEditLoading: boolean;
    syncing: boolean;

    // Manual "update latest data" refresh (§4.11) — a user-triggered pull of the freshest
    // catalogue when they've changed something directly in the PC app. `refreshedAt` drives
    // the "Updated just now" confirmation on the refresh button.
    refreshing: boolean;
    catalogueRefreshedAt: string | null;

    // Staged status text shown during send/confirm/pick — null when nothing is in flight.
    thinkingStatus: string | null;

    // The outcome of the last undo. A REJECTED undo (`ok: false`) keeps the user in the
    // activity panel and explains itself there via `undoResult` + `undoOk === false`; a
    // successful one is confirmed by the messages the backend appends to the thread, so the
    // panel banner is not used for it. Cleared when the user dismisses it.
    undoResult: string | null;
    undoOk: boolean | null;

    // Set to the conversation the backend wrote a SUCCESSFUL undo into. The shell watches
    // this, closes the activity panel onto that chat, then clears it — so it acts once per
    // undo and never fires for a rejected one.
    undoNavConversationId: string | null;

    /** True when older threads/entries exist beyond what is listed — drives "Show older". */
    conversationsHasMore: boolean;
    logHasMore: boolean;

    error: string | null;
    /** Bumped every time `error` is raised, even when the message is identical to the last one.
     *  The banner dismisses by remembering the message it hid, so pressing the same failing
     *  action twice produced the same string and stayed hidden — the second press looked like
     *  nothing happened at all. Dismissal is keyed on this instead, so a re-raised error always
     *  reappears while a genuinely unchanged one stays dismissed. */
    errorSeq: number;
}

const initialState: ProductCatalogChatbotState = {
    locationId: null,
    activeConversationId: null,
    conversation: null,
    conversationLoading: false,
    conversationFailure: false,

    conversations: [],
    conversationsLoading: false,
    conversationsFailure: false,

    channels: [],
    channelsLoaded: false,

    ctas: [],
    ctasLoaded: false,

    log: [],
    logLoading: false,

    createLoading: false,
    sending: false,
    cardActionLoading: false,
    cardEditLoading: false,
    syncing: false,

    refreshing: false,
    catalogueRefreshedAt: null,

    thinkingStatus: null,
    undoResult: null,
    undoOk: null,
    undoNavConversationId: null,

    conversationsHasMore: false,
    logHasMore: false,

    error: null,
    errorSeq: 0,
};

/** Replace an action card in-place within the active conversation's messages. */
function replaceCard(messages: PCMessage[], card: PCActionCard): void {
    const idx = messages.findIndex((m) => m.kind === 'action' && m.card.id === card.id);
    if (idx >= 0) {
        (messages[idx] as Extract<PCMessage, { kind: 'action' }>).card = card;
    }
}

export default function ProductCatalogChatbotReducer(state = initialState, action: any) {
    const errorBefore = state.error;
    const next = produce(state, (draft) => {
        switch (action.type) {
            // ── Create / resume ─────────────────────────────────────────────────
            case T.CREATE_CONVERSATION_REQUEST:
                draft.createLoading = true;
                draft.error = null;
                // "New chat" (fresh) — drop the current thread straight away so the
                // view resets instantly instead of showing the old one until the
                // server responds. A plain resume keeps what's on screen.
                if (action.payload?.fresh) {
                    draft.conversation = null;
                    draft.activeConversationId = null;
                }
                break;
            case T.CREATE_CONVERSATION_SUCCESS:
                draft.createLoading = false;
                draft.conversation = action.payload;
                draft.activeConversationId = action.payload.conversationId;
                break;
            case T.CREATE_CONVERSATION_FAILURE:
                draft.createLoading = false;
                draft.error = action.payload.error;
                break;

            // ── List / sidebar ──────────────────────────────────────────────────
            case T.LIST_CONVERSATIONS_REQUEST:
                draft.conversationsLoading = true;
                draft.conversationsFailure = false;
                break;
            case T.LIST_CONVERSATIONS_SUCCESS: {
                draft.conversationsLoading = false;
                const { conversations, hasMore, append } = action.payload;
                // A "show older" page is appended; a fresh load replaces. Deduped by id so a
                // thread that moved between pages (its updatedAt changed mid-scroll) can never
                // render twice.
                draft.conversations = append
                    ? [...draft.conversations, ...conversations.filter(
                        (c: PCConversationSummary) => !draft.conversations.some((x) => x.conversationId === c.conversationId))]
                    : conversations;
                draft.conversationsHasMore = !!hasMore;
                break;
            }
            case T.LIST_CONVERSATIONS_FAILURE:
                draft.conversationsLoading = false;
                draft.conversationsFailure = true;
                draft.error = action.payload.error;
                break;

            // ── Load one thread ─────────────────────────────────────────────────
            case T.LOAD_CONVERSATION_REQUEST:
                draft.conversationLoading = true;
                draft.conversationFailure = false;
                break;
            case T.LOAD_CONVERSATION_SUCCESS:
                draft.conversationLoading = false;
                draft.conversation = action.payload;
                draft.activeConversationId = action.payload.conversationId;
                break;
            case T.LOAD_CONVERSATION_FAILURE:
                draft.conversationLoading = false;
                draft.conversationFailure = true;
                draft.error = action.payload.error;
                break;

            // ── Send a message ──────────────────────────────────────────────────
            case T.SEND_MESSAGE_REQUEST:
                draft.sending = true;
                draft.error = null;
                break;
            case T.SEND_MESSAGE_SUCCESS:
                draft.sending = false;
                if (draft.conversation && draft.conversation.conversationId === action.payload.conversationId) {
                    // Reconcile action cards BY CARD ID, the same way confirm/pick already do.
                    // A new instruction supersedes whatever card was pending, and the server
                    // returns that card again in its retired state — pushing blindly appended a
                    // SECOND copy, so the original stayed on screen as 'pending' with live
                    // buttons beside a duplicate marked cancelled. Pressing those buttons then
                    // answered "That change was already handled" while the composer stayed
                    // locked on a card that no longer existed server-side.
                    for (const msg of action.payload.messages as PCMessage[]) {
                        const idx =
                            msg.kind === 'action'
                                ? draft.conversation.messages.findIndex(
                                      (m) => m.kind === 'action' && m.card.id === msg.card.id
                                  )
                                : -1;
                        if (idx >= 0 && msg.kind === 'action') {
                            (draft.conversation.messages[idx] as Extract<PCMessage, { kind: 'action' }>).card = msg.card;
                        } else {
                            draft.conversation.messages.push(msg);
                        }
                    }
                }
                break;
            case T.SEND_MESSAGE_FAILURE:
                draft.sending = false;
                draft.error = action.payload.error;
                break;

            // ── Rename / delete ─────────────────────────────────────────────────
            case T.RENAME_CONVERSATION_SUCCESS: {
                const { conversationId, title } = action.payload;
                const row = draft.conversations.find((c) => c.conversationId === conversationId);
                if (row) row.title = title;
                if (draft.conversation && draft.conversation.conversationId === conversationId) {
                    draft.conversation.title = title;
                }
                break;
            }
            case T.DELETE_CONVERSATION_SUCCESS: {
                const { conversationId } = action.payload;
                draft.conversations = draft.conversations.filter((c) => c.conversationId !== conversationId);
                if (draft.activeConversationId === conversationId) {
                    draft.activeConversationId = null;
                    draft.conversation = null;
                }
                break;
            }
            case T.RENAME_CONVERSATION_FAILURE:
            case T.DELETE_CONVERSATION_FAILURE:
                draft.error = action.payload.error;
                break;

            // ── Card ops: confirm appends messages; the rest replace the card ────
            case T.CONFIRM_CARD_REQUEST:
            case T.CANCEL_CARD_REQUEST:
            case T.PICK_CANDIDATE_REQUEST:
                draft.cardActionLoading = true;
                draft.error = null;
                break;

            // Row edits are optimistic — the card already shows the new state, so they must NOT
            // raise the global in-flight flag that blocks the composer and shows "thinking".
            case T.SET_CARD_CHANNELS_REQUEST:
            case T.SET_CARD_DURATION_REQUEST:
            case T.REMOVE_CARD_ROW_REQUEST:
                draft.cardEditLoading = true;
                draft.error = null;
                break;

            // confirm AND pick-candidate both return a message stream: the acted-on card in its
            // new state (reconciled in place, below), plus any NEW messages — a fresh
            // confirm/diagnostic card, a summary, a sync offer, or (for a multi-ambiguity
            // request) the NEXT queued picker, so the clarification queue surfaces one picker
            // per turn for free.
            case T.CONFIRM_CARD_SUCCESS:
            case T.PICK_CANDIDATE_SUCCESS:
                draft.cardActionLoading = false;
                if (draft.conversation && draft.conversation.conversationId === action.payload.conversationId) {
                    // The server re-sends the acted-on card under a NEW message id but the SAME
                    // card id, carrying its new status (e.g. committed / the retired picker).
                    // Reconcile those in place — appending blindly leaves the original `pending`
                    // copy on screen with live Confirm/Cancel buttons next to its committed twin.
                    for (const msg of action.payload.messages as PCMessage[]) {
                        const idx =
                            msg.kind === 'action'
                                ? draft.conversation.messages.findIndex(
                                      (m) => m.kind === 'action' && m.card.id === msg.card.id
                                  )
                                : -1;
                        if (idx >= 0 && msg.kind === 'action') {
                            (draft.conversation.messages[idx] as Extract<PCMessage, { kind: 'action' }>).card = msg.card;
                        } else {
                            draft.conversation.messages.push(msg);
                        }
                    }
                    // The BE-sent messages already carry any new pending card; a plain confirm
                    // clears the pending state. Recompute from the freshest pending card in the
                    // stream so a follow-up picker/confirm card stays actionable.
                    const pending = [...draft.conversation.messages].reverse().find(
                        (m: any) => m.kind === 'action' && (m.card?.status === 'pending' || m.card?.status === 'disambiguation')
                    );
                    draft.conversation.pendingCardId = (pending as any)?.card?.id ?? null;
                }
                break;

            case T.CANCEL_CARD_SUCCESS: {
                draft.cardActionLoading = false;
                draft.cardEditLoading = false;
                const { card, messages } = action.payload;
                if (draft.conversation && card) {
                    replaceCard(draft.conversation.messages, card);
                    // Cancelling a picker mid-queue drops only that clarification — the server
                    // sends back whatever carries the rest of the request forward. Append those
                    // (reconciling by card id, as the pick path does) so the untouched actions
                    // stay on screen instead of disappearing with the declined question.
                    for (const msg of (messages ?? []) as PCMessage[]) {
                        const idx =
                            msg.kind === 'action'
                                ? draft.conversation.messages.findIndex(
                                      (m) => m.kind === 'action' && m.card.id === msg.card.id
                                  )
                                : -1;
                        if (idx >= 0 && msg.kind === 'action') {
                            (draft.conversation.messages[idx] as Extract<PCMessage, { kind: 'action' }>).card = msg.card;
                        } else {
                            draft.conversation.messages.push(msg);
                        }
                    }
                    // Recompute from the freshest live card in the stream: null after an ordinary
                    // cancel, the follow-on picker/confirm card when the request continues.
                    const pending = [...draft.conversation.messages].reverse().find(
                        (m: any) => m.kind === 'action' && (m.card?.status === 'pending' || m.card?.status === 'disambiguation')
                    );
                    draft.conversation.pendingCardId = (pending as any)?.card?.id ?? null;
                }
                break;
            }

            case T.SET_CARD_CHANNELS_SUCCESS:
            case T.SET_CARD_DURATION_SUCCESS:
            case T.REMOVE_CARD_ROW_SUCCESS: {
                // Clear both: cancel raises cardActionLoading, the row edits raise cardEditLoading.
                draft.cardActionLoading = false;
                draft.cardEditLoading = false;
                const card = action.payload as PCActionCard;
                if (draft.conversation) {
                    replaceCard(draft.conversation.messages, card);
                    if (card.status === 'cancelled' || card.status === 'committed') {
                        if (draft.conversation.pendingCardId === card.id) draft.conversation.pendingCardId = null;
                    }
                }
                break;
            }

            case T.CONFIRM_CARD_FAILURE:
            case T.CANCEL_CARD_FAILURE:
            case T.PICK_CANDIDATE_FAILURE:
            case T.SET_CARD_CHANNELS_FAILURE:
            case T.SET_CARD_DURATION_FAILURE:
            case T.REMOVE_CARD_ROW_FAILURE:
                draft.cardActionLoading = false;
                draft.cardEditLoading = false;
                draft.error = action.payload.error;
                break;

            // ── Channels ────────────────────────────────────────────────────────
            case T.FETCH_CHANNELS_SUCCESS:
                draft.channels = action.payload;
                draft.channelsLoaded = true;
                break;
            // Deliberately does NOT set channelsLoaded — leaving it false lets the open-effect
            // retry the fetch instead of stranding the session with an empty channel list,
            // which would render every card's channel column as a raw id.
            case T.FETCH_CHANNELS_FAILURE:
                draft.error = action.payload.error;
                break;

            // ── Quick-action CTAs (§Item 6) ─────────────────────────────────────
            case T.FETCH_CTAS_SUCCESS:
                draft.ctas = action.payload;
                draft.ctasLoaded = true;
                break;
            case T.FETCH_CTAS_FAILURE:
                // Non-fatal — the intro simply falls back to its built-in prompts.
                draft.ctasLoaded = true;
                break;

            // ── Sync: update the sync card by its message id ─────────────────────
            case T.RUN_SYNC_REQUEST:
            case T.SYNC_RESULTS_REQUEST:
            case T.SYNC_RETRY_REQUEST:
                draft.syncing = true;
                break;
            case T.RUN_SYNC_SUCCESS:
            case T.SYNC_RESULTS_SUCCESS:
            case T.SYNC_RETRY_SUCCESS: {
                // Still running (and the composer stays blocked) until the RESULTS success —
                // the saga now chains trigger -> countdown -> results as one flow, so a
                // 'running' card from RUN_SYNC_SUCCESS/SYNC_RETRY_SUCCESS means the wait is
                // still in progress, not finished.
                draft.syncing = action.type !== T.SYNC_RESULTS_SUCCESS;
                const { msgId, card } = action.payload;
                if (draft.conversation) {
                    const idx = draft.conversation.messages.findIndex((m) => m.kind === 'sync' && m.id === msgId);
                    if (idx >= 0) {
                        (draft.conversation.messages[idx] as Extract<PCMessage, { kind: 'sync' }>).card = card;
                    }
                }
                break;
            }
            case T.RUN_SYNC_FAILURE:
            case T.SYNC_RESULTS_FAILURE:
            case T.SYNC_RETRY_FAILURE:
                draft.syncing = false;
                draft.error = action.payload.error;
                break;
            case T.CHATBOT_SYNC_TICK: {
                const { msgId, remaining } = action.payload;
                if (draft.conversation) {
                    const idx = draft.conversation.messages.findIndex((m) => m.kind === 'sync' && m.id === msgId);
                    if (idx >= 0) {
                        const syncMsg = draft.conversation.messages[idx] as Extract<PCMessage, { kind: 'sync' }>;
                        syncMsg.card.summary = `Syncing… ${remaining}s`;
                    }
                }
                break;
            }

            // ── Activity log & undo ─────────────────────────────────────────────
            case T.FETCH_LOG_REQUEST:
                draft.logLoading = true;
                break;
            case T.FETCH_LOG_SUCCESS: {
                draft.logLoading = false;
                const { entries, hasMore, append } = action.payload;
                draft.log = append
                    ? [...draft.log, ...entries.filter((e: PCLogEntry) => !draft.log.some((x) => x.id === e.id))]
                    : entries;
                draft.logHasMore = !!hasMore;
                break;
            }
            case T.FETCH_LOG_FAILURE:
                draft.logLoading = false;
                draft.error = action.payload.error;
                break;
            case T.UNDO_LOG_ENTRY_SUCCESS: {
                // The request resolved — but `ok` decides whether the change was actually
                // reversed. `ok: false` is a rejection, not a transport failure.
                const { entryId, result } = action.payload;
                draft.undoOk = !!result?.ok;
                if (!result?.ok) {
                    // Rejected: keep the user in the activity panel and explain it there.
                    draft.undoResult = result?.message ?? 'That change could not be undone.';
                    break;
                }
                // Reflect the reversal locally; the saga also re-fetches the log for truth.
                const entry = draft.log.find((l) => l.id === entryId);
                if (entry) entry.state = 'undone';
                // The backend wrote the reversal into `result.conversationId` and handed back the
                // messages it appended ("Undone: …" + a fresh sync offer, since the undo itself
                // needs syncing). Splice them into the open thread when it is that thread; if it
                // isn't, the saga re-fetches it, so appending here would double up.
                // Prefer the response's own echo; fall back to the thread the request was sent
                // for, so routing still works if the backend omits it.
                const target = result?.conversationId ?? action.payload.conversationId ?? null;
                if (target && draft.conversation?.conversationId === target && result?.messages?.length) {
                    const seen = new Set(draft.conversation.messages.map((m) => m.id));
                    draft.conversation.messages.push(...result.messages.filter((m: PCMessage) => !seen.has(m.id)));
                }
                // Route the user to that chat. The appended messages are the confirmation, so the
                // panel banner is cleared rather than shown on the way out.
                draft.undoNavConversationId = target;
                draft.undoResult = null;
                break;
            }
            case T.UNDO_LOG_ENTRY_FAILURE:
                draft.error = action.payload.error;
                break;

            // ── Manual catalogue refresh ────────────────────────────────────────
            case T.REFRESH_CATALOGUE_REQUEST:
                draft.refreshing = true;
                draft.error = null;
                break;
            case T.REFRESH_CATALOGUE_SUCCESS:
                draft.refreshing = false;
                draft.catalogueRefreshedAt = action.payload?.refreshedAt ?? new Date().toISOString();
                break;
            case T.REFRESH_CATALOGUE_FAILURE:
                draft.refreshing = false;
                draft.error = action.payload.error;
                break;

            // ── Local view-only mutations ───────────────────────────────────────
            case T.CHATBOT_SET_ACTIVE_CONVERSATION:
                draft.activeConversationId = action.payload;
                break;
            case T.CHATBOT_APPEND_LOCAL_MESSAGE:
                if (draft.conversation) draft.conversation.messages.push(action.payload);
                break;
            case T.CHATBOT_THINKING_STATUS:
                draft.thinkingStatus = action.payload;
                break;
            case T.CHATBOT_OPTIMISTIC_CARD_PATCH: {
                const { cardId, patch } = action.payload;
                if (!draft.conversation) break;
                const idx = draft.conversation.messages.findIndex((m) => m.kind === 'action' && m.card.id === cardId);
                if (idx < 0) break;
                const msg = draft.conversation.messages[idx] as Extract<PCMessage, { kind: 'action' }>;
                msg.card = { ...msg.card, ...patch } as PCActionCard;
                break;
            }
            case T.CHATBOT_PREDICT_TITLE:
                if (draft.conversation) draft.conversation.title = action.payload;
                break;
            case T.CHATBOT_CLEAR_UNDO_RESULT:
                draft.undoResult = null;
                draft.undoOk = null;
                break;
            case T.CHATBOT_CLEAR_UNDO_NAV:
                draft.undoNavConversationId = null;
                break;
            case T.CHATBOT_RESET:
                // Reset to a clean slice, but REMEMBER which outlet it is now for - otherwise the
                // very next render cannot tell "never loaded" from "loaded for another outlet".
                return { ...initialState, locationId: action.payload?.locationId ?? null };

            default:
                return state;
        }
    });
    // An error raised by THIS action counts as a new one for the banner — including a repeat of
    // the identical message, which is the case that used to render nothing. The banner keys its
    // dismissal on this counter rather than on the text. Gated on the action actually being a
    // failure, so an unrelated action passing through while an error is on screen doesn't
    // resurrect a banner the user already dismissed.
    const raisedByThisAction = typeof action.type === 'string' && action.type.endsWith('_FAILURE');
    if (next.error && (next.error !== errorBefore || raisedByThisAction)) {
        return { ...next, errorSeq: next.errorSeq + 1 };
    }
    return next;
}
